// Fachsprache und Fehlererholung der Schnellerfassung (ST-PTY-001).
//
// WARUM DIESE DATEI GETRENNT IST -- derselbe Grund wie bei access.ts: NFR-UX-003
// verbietet Technikbegriffe auf der Kernoberflaeche, und ein Grundcode ist eine
// stabile Kennung fuer Log und Audit, kein Satz fuer Menschen. Laege die
// Uebersetzung in der Komponente, entstuende sie bei jedem neuen Formular
// erneut, und ein Grundcode ohne Satz wuerde irgendwann durchgereicht.
//
// DAS WORT "PARTY" KOMMT HIER NICHT VOR. Es ist der Modellname; auf dem
// Bildschirm heisst dasselbe "Kontakt". NFR-UX-003 nennt genau dieses Wort als
// Beispiel, und apps/web/test/quick-capture.test.ts prueft es.

import { correlationOf, type PlainMessage, type ResponseFacts } from './access.ts';
import { validationMessageFor as addressValidationMessageFor } from './addresses.ts';
import { artWort, type HausArt } from './haus-art.ts';
import { organisationAngabenFuerSchluessel, organisationAngabenRumpf, type OrganisationAngaben } from './organisation-angaben.ts';

/** Was die Anwendung dem Menschen sagt, und was er tun kann. Wie in access.ts. */
export interface FieldMessage {
  /** Der Feldname in Fachsprache, so wie er am Eingabefeld steht. */
  readonly label: string;
  /** Was mit der Eingabe nicht stimmt, und was jetzt zu tun ist. */
  readonly hint: string;
}

/**
 * Die Feldpfade der API in der Sprache der Oberflaeche.
 *
 * Der Schluessel ist der Feldpfad der Anfrage, wie ihn die 422-Antwort nennt --
 * und wie ihn `QUICK_CAPTURE_FIELD_PATHS` in apps/api deklariert. Ein hier
 * fehlender Pfad ist eine Luecke, die der Test findet, nicht ein haesslicher
 * String auf dem Bildschirm.
 */
export const FIELD_LABELS: Readonly<Record<string, string>> = Object.freeze({
  kind: 'Art des Kontakts',
  displayName: 'Anzeigename',
  person: 'Name',
  'person.givenName': 'Vorname',
  'person.familyName': 'Nachname',
  organization: 'Organisation',
  'organization.legalName': 'Name der Organisation',
  email: 'E-Mail',
  phone: 'Telefon',
  // Die Geschaeftsanschrift ("Anschrift einfuegen", 29.09.2026) -- dieselben
  // Beschriftungen wie auf dem Bildschirm "Anschriften" (adressen.tsx), damit
  // dieselbe Angabe an beiden Stellen gleich heisst.
  address: 'Geschäftsanschrift',
  'address.usageCode': 'Geschäftsanschrift',
  'address.countryCode': 'Land',
  'address.addressLine1': 'Straße und Hausnummer',
  'address.addressLine2': 'Adresszusatz',
  'address.postalCode': 'Postleitzahl',
  'address.locality': 'Ort',
  // Die Verknuepfung "arbeitet für" (ST-REL-001).
  worksForPartyId: 'Arbeitet für',
  // Die Leitung (Signatur S3, leads); die Haekchen baut SEITEN.
  leadsPartyId: 'Leitet',
  // Die Funktion an dieser Verknuepfung (Signatur S1b).
  roleCode: 'Funktion',
  // Die Angaben der neuen Organisation (Signatur S2); die Felder baut SEITEN.
  website: 'Website',
  commercialRegister: 'Handelsregister',
  'commercialRegister.issuer': 'Registergericht',
  'commercialRegister.value': 'Handelsregister',
  vatId: 'USt-IdNr.',
  'vatId.issuer': 'USt-IdNr.',
  'vatId.value': 'USt-IdNr.',
});

/**
 * Die Gruende der API in der Sprache der Oberflaeche.
 *
 * `{feld}` wird durch den Feldnamen ersetzt. Die Saetze sind bewusst
 * feldunabhaengig formuliert: der Katalog in apps/api ist ausdruecklich
 * GENERISCH, weil der Rahmen kein Schema-DSL besitzt (ADR-0019 Nachtrag). Eine
 * feldspezifische Sonderformulierung hier wuerde diese Trennung wieder
 * aufweichen -- wer sie braucht, braucht in Wahrheit ein anderes Feld.
 */
export const VALIDATION_HINTS: Readonly<Record<string, string>> = Object.freeze({
  field_required: 'Bitte fülle „{feld}“ aus. Ohne diese Angabe lässt sich der Kontakt nicht anlegen.',
  field_malformed: 'Die Eingabe unter „{feld}“ hat nicht die erwartete Form. Bitte prüfe sie und versuche es erneut.',
  field_too_long: 'Die Eingabe unter „{feld}“ ist zu lang. Bitte kürze sie auf das Wesentliche.',
  field_value_unsupported: 'Der gewählte Wert für „{feld}“ ist nicht vorgesehen. Bitte wähle einen der angebotenen.',
  field_not_allowed: 'Das Feld „{feld}“ passt nicht zur gewählten Art des Kontakts. Bitte entferne die Eingabe.',
  field_combination_invalid: 'Die Angaben unter „{feld}“ passen nicht zusammen. Bitte prüfe sie gemeinsam.',
});

export const TRANSLATED_VALIDATION_REASON_CODES: readonly string[] = Object.freeze(Object.keys(VALIDATION_HINTS));

/**
 * Rueckfall fuer einen unbekannten Feldpfad.
 *
 * Er zeigt den Pfad NICHT an. Ein roher Feldpfad auf dem Bildschirm waere
 * gleich zweimal falsch: Technikbegriff (NFR-UX-003) und eine Auskunft, mit
 * der niemand etwas anfangen kann.
 */
export const UNKNOWN_FIELD_LABEL = 'eine Angabe';

/** Rueckfall fuer einen unbekannten Grundcode. Ebenfalls ohne den Code selbst. */
export const UNKNOWN_VALIDATION_HINT =
  'Eine deiner Angaben konnte nicht übernommen werden. Bitte prüfe das Formular und versuche es erneut.';

export function fieldLabelFor(fieldPath: string): string {
  return FIELD_LABELS[fieldPath] ?? UNKNOWN_FIELD_LABEL;
}

export function validationMessageFor(fieldPath: string, reasonCode: string): FieldMessage {
  const label = fieldLabelFor(fieldPath);
  // Eine Anschrift bekommt DIESELBEN Saetze wie auf dem Bildschirm "Anschriften"
  // -- sie sagen, WAS zu aendern ist ("Trage den Ort ein."), statt nur, dass
  // etwas fehlt. Eine zweite Fassung hier liefe beim naechsten Laenderschnitt
  // auseinander. Ausnahme: der mitgeschickte Zweck, den dieser Bildschirm nie
  // schickt -- ein fremder Client bekommt den allgemeinen Satz.
  if (fieldPath.startsWith('address.') && fieldPath !== 'address.usageCode') {
    return { label, hint: addressValidationMessageFor(fieldPath.slice('address.'.length), reasonCode) };
  }
  // Die Organisation ist nicht getippt, sondern gewaehlt: der allgemeine Satz
  // ("wähle einen der angebotenen") passte nicht. Derselbe Satz fuer "gibt es
  // nicht", "darfst du nicht sehen" und "ist keine Organisation" -- der Server
  // unterscheidet sie bewusst nicht.
  if (fieldPath === 'worksForPartyId' && reasonCode === 'field_value_unsupported') {
    return { label, hint: WORKS_FOR_UNUSABLE_HINT };
  }
  if ((fieldPath === 'commercialRegister.value' || fieldPath === 'vatId.value') && reasonCode === 'field_value_unsupported') {
    return { label, hint: kennungSchonVergebenHinweis() };
  }
  const template = VALIDATION_HINTS[reasonCode];
  return {
    label,
    hint: template === undefined ? UNKNOWN_VALIDATION_HINT : template.replace('{feld}', label),
  };
}

/**
 * Handelsregister oder USt-IdNr. fuehrt schon eine Organisation der Gruppe
 * (Vertrag #746, Ansage 4 A). VORLAEUFIG bis Michaels Entscheidung M9: der Satz
 * nennt kein Haus und fuehrt nicht in eine Sackgasse, auch wenn die
 * Organisation unter "Arbeitet für" nicht sichtbar ist (DESIGN, Dirigent 23.10.).
 */
export function kennungSchonVergebenHinweis(art?: HausArt | null): string {
  return `Diese Nummer ist in LIAISON schon vergeben. Prüf sie, oder wähl die Organisation unter „Arbeitet für“. Findest du sie dort nicht, wende dich an die Pflege ${artWort(art, 'deines')}.`;
}

/**
 * Zusammenfassung fuer die Statusmeldung ueber dem Formular.
 *
 * NFR-ACC-001: eine Fehlermarkierung am einzelnen Feld allein reicht nicht --
 * wer mit einem Screenreader arbeitet oder gerade weit unten im Formular steht,
 * erfaehrt sonst nicht, DASS etwas nicht angenommen wurde. Die Zusammenfassung
 * steht in einer Region mit aria-live und nennt die Anzahl.
 */
export function validationSummary(count: number): string {
  if (count <= 0) {
    return 'Der Kontakt konnte nicht angelegt werden.';
  }
  return count === 1
    ? 'Eine Angabe konnte nicht übernommen werden. Sie ist unten markiert.'
    : `${count} Angaben konnten nicht übernommen werden. Sie sind unten markiert.`;
}

// ---------------------------------------------------------------------------
// Der Aufruf der Anlage
// ---------------------------------------------------------------------------

export type PartyKind = 'person' | 'organization';

/**
 * Die Geschaeftsanschrift im Formular -- dieselben fuenf Felder wie der
 * Adressbefehl ohne Zweck. `countryCode` ist '' , wenn das Land nicht
 * eindeutig ist; dann waehlt die Nutzerin.
 */
export interface AddressFields {
  readonly countryCode: string;
  readonly addressLine1: string;
  readonly addressLine2: string;
  readonly postalCode: string;
  readonly locality: string;
}

/** Was das Formular haelt. Alles optional ausser der Art -- CM-CAP-001. */
export interface QuickCaptureForm {
  readonly kind: PartyKind;
  readonly givenName: string;
  readonly familyName: string;
  readonly legalName: string;
  readonly email: string;
  readonly phone: string;
  /**
   * Fehlt, solange keine Anschrift erkannt oder sie nicht uebernommen wurde.
   * Es gibt bewusst kein leeres Anschriftformular in der Anlage: der
   * Pflegeweg dafuer ist "Anschriften" (progressive Offenlegung, NFR-UX-004).
   */
  readonly address?: AddressFields | undefined;
}

/**
 * Die Organisation, fuer die die neue Person arbeitet (ST-REL-001). Der Name
 * dient nur der Anzeige und der Bestaetigung; geschickt wird die Kennung.
 */
export interface WorksForChoice {
  readonly partyId: string;
  readonly displayName: string;
  /** Der Organisationsname aus der Signatur, wenn die Wahl aus seiner Suche stammt oder mit ihm angelegt wurde. */
  readonly fromSuggestion?: string | undefined;
}

/** Der Organisationsname der Signatur, den diese Wahl uebernimmt; sonst `undefined`. */
export function takenSuggestion(choice: WorksForChoice | undefined, suggestion: string | undefined): string | undefined {
  if (choice === undefined || suggestion === undefined) return undefined;
  const name = suggestion.trim();
  return choice.fromSuggestion?.trim() === name || choice.displayName.trim() === name ? name : undefined;
}

/**
 * Die vorbelegte Organisation fuer "Ansprechpartner anlegen" (Slice 6b): nur im
 * Speicher des Tabs, nie in der Adresse (wie der Suchbegriff, parties.ts), und
 * nur bis die Schnellerfassung sie uebernommen hat. Ob die Wahl beim Anlegen
 * taugt, entscheidet der Server wie bei jeder Wahl aus der Suche.
 */
let worksForPreset: WorksForChoice | undefined;

export function presetWorksFor(choice: WorksForChoice): void {
  worksForPreset = choice;
}

/** Liest die Vorbelegung, ohne sie zu verbrauchen (React darf den Anfangswert doppelt rechnen). */
export function peekWorksForPreset(): WorksForChoice | undefined {
  return worksForPreset;
}

export function clearWorksForPreset(): void {
  worksForPreset = undefined;
}

export const WORKS_FOR_UNUSABLE_HINT =
  'Mit dieser Organisation lässt sich hier keine Beziehung festhalten. Suche sie erneut und wähle sie aus den Treffern, oder lass das Feld leer.';

/**
 * Was ein neuer Kontakt zunaechst ist: ein Hauskontakt des eigenen Hauses
 * (ADR-0064 Ziffer 3). Geteilt mit jedem Weg, der Kontakte anlegt (DESIGN
 * Slice 7, Anlage aus einer Signatur), damit der Satz ueberall derselbe ist.
 * Er verspricht keinen Rueckweg: "Fuer alle Haeuser freigeben" kommt erst mit
 * ST-SEC-001 S4. Ohne bekannten Namen nennt er die Art ("deinen Verlag", A2);
 * ein leerer Name zaehlt als fehlend.
 */
export function captureVisibilityNotice(houseName?: string, art?: HausArt | null): string {
  return `Der Kontakt ist zunächst nur für ${eigenesHaus(houseName, art)} sichtbar.`;
}

/** Dieselbe Aussage in der Bestaetigung nach der Anlage, hinter "Der Kontakt ist als Entwurf gespeichert." -- ohne den Kontakt ein zweites Mal zu nennen. */
export function capturedVisibilityLine(houseName?: string, art?: HausArt | null): string {
  return `Zunächst nur für ${eigenesHaus(houseName, art)} sichtbar.`;
}

function eigenesHaus(houseName: string | undefined, art: HausArt | null | undefined): string {
  return houseName === undefined || houseName.length === 0 ? artWort(art, 'deinen') : houseName;
}

/**
 * Mehrere Haeuser: vor der Anlage KEIN Satz "nur fuer dein Haus" -- er waere
 * falsch, denn die Anlage endet dann mit 409 (HOUSE_AMBIGUOUS_CAPTURE_MESSAGE).
 * Stattdessen sagt der Abschnitt vorab, was hier noch nicht geht, und wer
 * jetzt weiterhilft.
 */
export const MULTI_HOUSE_CAPTURE_NOTICE =
  'Für welche Gesellschaft der Gruppe ein neuer Kontakt angelegt wird, ist hier noch nicht wählbar. Wird ein Kontakt jetzt gebraucht, wende dich an die LIAISON-Administration.';

/**
 * Mehrere Haeuser: die benannte Grenze dieses Schnitts, dieselbe wie bei Notiz
 * und Rolle. Der Satz sagt, warum nichts angelegt wurde, und was jetzt geht.
 */
export const HOUSE_AMBIGUOUS_CAPTURE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Für welche Gesellschaft der Gruppe der Kontakt angelegt wird, lässt sich hier noch nicht wählen.',
  explanation: 'Du arbeitest für mehrere Gesellschaften der Gruppe, und ein neuer Kontakt gehört zunächst genau einer davon. Es wurde nichts angelegt.',
  nextStep: 'Wird der Kontakt jetzt gebraucht, wende dich an die LIAISON-Administration.',
  retryable: false,
});

/**
 * Die Konflikte der Verknuepfung -- je Grund ein eigener Satz mit naechstem
 * Schritt (NFR-UX-005). Beide sagen, dass NICHTS angelegt wurde: die Anlage
 * und die Verknuepfung entstehen zusammen oder gar nicht.
 */
export const WORKS_FOR_REJECTIONS: Readonly<Record<string, PlainMessage>> = Object.freeze({
  counterpart_not_writable: Object.freeze({
    headline: 'Mit dieser Organisation lässt sich keine Beziehung festhalten.',
    explanation: 'Die Organisation ist zusammengeführt, archiviert oder anonymisiert. Es wurde nichts angelegt.',
    nextStep: 'Wähle die aktuelle Organisation oder entferne die Beziehung und lege den Kontakt erneut an.',
    retryable: false,
  }),
  relationship_limit_reached: Object.freeze({
    headline: 'Die Grenze für Beziehungen in dieser Stunde ist erreicht.',
    explanation: 'LIAISON begrenzt, wie viele Beziehungen eine Person je Stunde festhält. Es wurde nichts angelegt.',
    nextStep: 'Entferne die Beziehung, um den Kontakt jetzt anzulegen, und halte sie später unter „Beziehungen“ fest.',
    retryable: false,
  }),
});

// --- Die Karte unter "Arbeitet für" -----------------------------------------

/** Überschrift der Trefferliste und zugleich ihre Ansage. */
export function worksForHitsHeading(organizations: number): string {
  return organizations === 1 ? 'Eine passende Organisation:' : `${organizations} passende Organisationen:`;
}

export const WORKS_FOR_NO_MATCH = 'Keine passende Organisation.';

/** Mit weiteren Treffern kann die passende dahinter stehen: dann kein "neu anlegen" (die Suche ist die Dublettenprüfung). */
export const WORKS_FOR_MORE_HITS = 'Es gibt weitere Treffer. Gib mehr vom Namen ein.';

/** Die Ansage nach einer Suche ohne Treffer; mit Vorauswahl nennt sie, was mit dem Kontakt entsteht. */
export function worksForNoMatchAnnouncement(newName: string | undefined): string {
  return newName === undefined
    ? 'Keine passende Organisation gefunden.'
    : `Keine passende Organisation gefunden. „${newName}“ wird mit dem Kontakt angelegt.`;
}

/** Was eine Trefferzeile unter dem Namen trägt: Kontaktweg, sonst nichts; ein Rechtsnamen-Treffer sagt, warum er passt. */
export function worksForHitCaption(hit: { readonly primaryEmailAddress?: string; readonly primaryPhoneNumber?: string; readonly matchedLegalName?: string }): string | undefined {
  const teile = [hit.primaryEmailAddress ?? hit.primaryPhoneNumber, hit.matchedLegalName === undefined ? undefined : `Rechtsname: ${hit.matchedLegalName}`];
  const caption = teile.filter((teil): teil is string => teil !== undefined && teil !== '').join(' · ');
  return caption === '' ? undefined : caption;
}

export function createOrganizationLabel(name: string): string {
  return `„${name}“ neu anlegen`;
}

export function noneOfTheseLabel(name: string): string {
  return `Keine davon – „${name}“ neu anlegen`;
}

export const NEW_ORGANIZATION_CARD = 'Gibt es in LIAISON noch nicht. Wird mit dem Kontakt angelegt.';

// --- Vorschau "Das wird angelegt" -------------------------------------------

export const PREVIEW_HEADING = 'Das wird angelegt';
export const PREVIEW_NO_NAME = 'Ohne Namen';
export const PREVIEW_WORKS_FOR = 'arbeitet für';
export const PREVIEW_ORGANIZATION_LINE = 'Organisation · Entwurf';

/** Die Zeile unter der Person: Art, Stand, die Funktion an "Arbeitet für" und der erste Kontaktweg. */
export function previewPersonLine(firstContact: string | undefined, funktion?: string): string {
  const weg = firstContact?.trim();
  // Reihenfolge nach DESIGN (Vorgabe S2/S3): Art · Stand · Funktion · erster Kontaktweg.
  return ['Person', 'Entwurf', ...(funktion === undefined ? [] : [funktion]), ...(weg === undefined || weg === '' ? [] : [weg])].join(' · ');
}

/** Entstehen Kontakt und Organisation zusammen, sagt die Hauptaktion es. */
export const CREATE_BOTH_LABEL = 'Beide anlegen';

/**
 * Die Beschriftung der Hauptaktion: sie sagt, was der naechste Druck tut --
 * nach einer Dublettenwarnung "trotzdem", mit neuer Organisation "beide".
 */
export function createActionLabel(warningSeen: boolean, newOrganization: boolean, labels: { readonly create: string; readonly createAnyway: string }): string {
  if (warningSeen) return newOrganization ? 'Trotzdem beide anlegen' : labels.createAnyway;
  return newOrganization ? CREATE_BOTH_LABEL : labels.create;
}

export const WORKS_FOR_SEARCH_UNREACHABLE = 'Die Suche ist gerade nicht erreichbar. Prüfe deine Verbindung und suche erneut.';

/** Die Bestaetigung der Verknuepfung: einmal gespeichert, auf beiden Seiten lesbar (CM-REL-002). */
export function worksForSaved(displayName: string): string {
  return `Arbeitet für ${displayName}. Die Beziehung steht auch bei ${displayName}.`;
}

// --- Organisation mitanlegen (Vorgabe DESIGN O1-O7, 16.10.) -------------------
//
// ZWEI BESTEHENDE AUFRUFE, KEIN NEUER WEG: erst die Organisation, dann die
// Person mit `worksForPartyId` der neuen Organisation. Person und Beziehung
// entstehen damit zusammen; den einzigen Teilerfolg -- Organisation da, Person
// abgelehnt -- holt der zweite Versuch nach, ohne eine zweite Organisation.

/**
 * Das Formular, mit dem die Organisation angelegt wird: der Name und die
 * erkannte Geschaeftsanschrift (X7). Sie gehoert der Organisation, die Person
 * erbt sie (yaml 637).
 */
export function organizationForm(name: string, address?: AddressFields): QuickCaptureForm {
  return { ...EMPTY_FORM, kind: 'organization', legalName: name, ...(address === undefined ? {} : { address }) };
}

export function createdBothTitle(person: string, organization: string): string {
  return `Angelegt: ${person} und ${organization}`;
}

/** Zweite Zeile der Bestätigung, wenn Kontakt und Organisation zusammen entstanden sind. */
export function bothSavedLine(houseName?: string, art?: HausArt | null): string {
  return `Beide als Entwurf, zunächst nur für ${eigenesHaus(houseName, art)} sichtbar.`;
}

const NOTHING_CREATED = 'Es wurde nichts angelegt.';
export const CONTACT_NOT_CREATED = 'Der Kontakt wurde nicht angelegt.';

export interface PartialCaptureMessage {
  readonly headline: string;
  /** Der Grund der Person; "Es wurde nichts angelegt." ist hier falsch und ersetzt. */
  readonly reason: readonly string[];
  readonly nextStep: string;
}

/**
 * Der Teilerfolg (O4): die Organisation steht, die Person nicht. Jeder Satz,
 * der "nichts angelegt" behauptet, wird ersetzt; fehlt er, steht der richtige
 * am Ende des Grundes. `grund` fehlt bei Feldfehlern (die stehen am Feld).
 */
export function partialCaptureMessage(organization: string, grund: PlainMessage | undefined, keepsLink: boolean, markedFields = 0): PartialCaptureMessage {
  const ohneNichts = (satz: string): string => satz.replaceAll(NOTHING_CREATED, CONTACT_NOT_CREATED);
  // Feldfehler stehen am Feld; die eine Leiste sagt nur, dass es sie gibt (DESIGN P2-2 zu #654).
  const markiert = markedFields === 1 ? 'Eine Angabe ist unten markiert.' : `${markedFields} Angaben sind unten markiert.`;
  const reason =
    grund === undefined
      ? markedFields > 0
        ? [`${markiert} ${CONTACT_NOT_CREATED}`]
        : []
      : [ohneNichts(grund.headline), ohneNichts(grund.explanation)].filter((satz) => satz.trim() !== '');
  const mitSatz = reason.some((satz) => satz.includes(CONTACT_NOT_CREATED)) ? reason : [...reason, CONTACT_NOT_CREATED];
  return {
    headline: `Die Organisation „${organization}“ ist angelegt, der Kontakt noch nicht.`,
    reason: mitSatz,
    // Eine Ablehnung der Beziehung selbst behaelt ihren eigenen Schritt: die
    // Verknuepfung mit der neuen Organisation zu versprechen, waere dort falsch.
    nextStep:
      keepsLink || grund === undefined
        ? `Korrigiere die Angabe und lege den Kontakt erneut an. Er wird dann mit „${organization}“ verknüpft; eine zweite Organisation entsteht nicht.`
        : ohneNichts(grund.nextStep),
  };
}

/** Der Name taugt nicht als Organisation (O5): nichts ist angelegt, das Kaestchen bleibt. */
export function organizationInvalid(organization: string, errors: readonly FieldError[]): PlainMessage {
  const first = errors.find((error) => error.fieldPath.startsWith('organization')) ?? errors[0];
  return {
    headline: `„${organization}“ lässt sich so nicht als Organisation anlegen.`,
    explanation: `${first === undefined ? '' : `${validationMessageFor('organization.legalName', first.reasonCode).hint} `}${NOTHING_CREATED}`,
    nextStep: 'Ändere den Namen unter „Arbeitet für“ und suche erneut.',
    retryable: false,
  };
}

/**
 * Der Schluessel, unter dem die Absendekennung gehalten wird: der Rumpf so
 * normalisiert, wie der Server ihn fuer den Fingerprint liest. Die Anschrift
 * bildet NFC (Adressbefehl), Namen und Kontaktwege sind nur getrimmt -- der
 * Rumpf ist es schon. Staerker zu normalisieren als der Server hiesse
 * client_request_reused fuer eine Eingabe, die der Server anders sieht.
 */
export function captureRequestKey(rumpf: Readonly<Record<string, unknown>>): string {
  const body = organisationAngabenFuerSchluessel(rumpf);
  const address = body['address'] as Readonly<Record<string, string>> | undefined;
  return JSON.stringify(
    address === undefined
      ? body
      : { ...body, address: Object.fromEntries(Object.entries(address).map(([feld, wert]) => [feld, wert.normalize('NFC')])) },
  );
}

/**
 * Die Antwort kam nicht an: ob angelegt wurde, weiss der Browser nicht. Der Satz
 * sagt das und fuehrt auf den Weg, der es klaert -- derselbe Inhalt erneut, mit
 * derselben Kennung, legt nichts doppelt an (ADR-0060).
 */
export const CAPTURE_UNREACHABLE: PlainMessage = Object.freeze({
  headline: 'Ob der Kontakt angelegt wurde, ist offen.',
  explanation: 'Die Antwort kam nicht an.',
  nextStep: 'Lege ihn mit denselben Angaben erneut an; doppelt angelegt wird dabei nichts.',
  retryable: true,
});

export function organizationUnreachable(organization: string): PlainMessage {
  return {
    headline: `Ob die Organisation „${organization}“ angelegt wurde, ist offen.`,
    explanation: 'Die Antwort kam nicht an. Der Kontakt ist noch nicht angelegt.',
    nextStep: 'Lege den Kontakt mit denselben Angaben erneut an; die Organisation entsteht dabei nicht doppelt.',
    retryable: true,
  };
}

/** Organisation angelegt, die Antwort zur Person kam nicht an. */
export function partialUncertainMessage(organization: string): PartialCaptureMessage {
  return {
    headline: `Die Organisation „${organization}“ ist angelegt. Ob auch der Kontakt angelegt wurde, ist offen.`,
    reason: ['Die Antwort kam nicht an.'],
    nextStep: 'Lege den Kontakt mit denselben Angaben erneut an; doppelt angelegt wird dabei nichts.',
  };
}

/** Die beiden Konflikte der Absendekennung beim Anlegen (ADR-0060). */
export const CAPTURE_REQUEST_REUSED: PlainMessage = Object.freeze({
  headline: 'Dein erster Versuch ist mit anderen Angaben angekommen.',
  explanation: 'Deine jetzige Eingabe wurde nicht angelegt. Sie steht noch im Formular.',
  nextStep: 'Lade die Kontakte neu und prüfe, ob der Kontakt schon dasteht.',
  retryable: false,
});

export const CAPTURE_REQUEST_SUPERSEDED: PlainMessage = Object.freeze({
  headline: 'Der Kontakt ist angekommen und inzwischen geändert.',
  explanation: 'Dein erster Versuch wurde angelegt; seither hat jemand den Kontakt bearbeitet. Das Formular ist geleert.',
  nextStep: 'Such den Kontakt unter „Kontakte“, um zu sehen, was jetzt gilt.',
  retryable: false,
});

export function organizationRequestSuperseded(organization: string): PlainMessage {
  return {
    headline: `Die Organisation „${organization}“ ist angekommen und inzwischen geändert.`,
    explanation: 'Der Kontakt wurde nicht angelegt.',
    nextStep: 'Suche die Organisation unter „Arbeitet für“ erneut, wähle sie und lege den Kontakt an.',
    retryable: false,
  };
}

export const EMPTY_FORM: QuickCaptureForm = Object.freeze({
  kind: 'person',
  givenName: '',
  familyName: '',
  legalName: '',
  email: '',
  phone: '',
});

export interface FieldError {
  readonly fieldPath: string;
  readonly reasonCode: string;
}

export interface CreatedContact {
  readonly channel: string;
}

export type QuickCaptureResult =
  | {
      readonly kind: 'created';
      readonly partyId: string;
      readonly displayName: string;
      readonly contacts: readonly CreatedContact[];
      /** Wahr, wenn die Antwort eine angelegte Adressverwendung nennt. */
      readonly addressCreated: boolean;
    }
  | { readonly kind: 'invalid'; readonly errors: readonly FieldError[] }
  /** Eine Ablehnung oder Stoerung mit stabilem Grundcode; die Uebersetzung liegt in access.ts. */
  | { readonly kind: 'rejected'; readonly reasonCode: string | undefined; readonly facts: ResponseFacts };

/**
 * Baut den Anfragerumpf.
 *
 * LEERE FELDER WERDEN WEGGELASSEN und nicht als leerer String geschickt. Der
 * Unterschied ist fachlich: ein weggelassenes Feld heisst "nicht bekannt", ein
 * leerer String hiesse "ausdruecklich leer". CM-CAP-001 verlangt das erste --
 * unbekannte Angaben verhindern die Anlage nicht.
 *
 * Der Subtyp wird nur fuer die GEWAEHLTE Art mitgeschickt. Beide zu schicken
 * waere genau der Fall, den die API mit `field_not_allowed` abweist: eine Party
 * kann nicht gleichzeitig beide Subtypen besitzen (CM-PTY-002).
 */
export function buildRequestBody(
  form: QuickCaptureForm,
  worksFor?: WorksForChoice,
  roleCode?: string,
  angaben?: OrganisationAngaben,
  leadsPartyId?: string,
): Record<string, unknown> {
  const trimmed = {
    givenName: form.givenName.trim(),
    familyName: form.familyName.trim(),
    legalName: form.legalName.trim(),
    email: form.email.trim(),
    phone: form.phone.trim(),
  };
  const person: Record<string, string> = {};
  if (trimmed.givenName.length > 0) {
    person['givenName'] = trimmed.givenName;
  }
  if (trimmed.familyName.length > 0) {
    person['familyName'] = trimmed.familyName;
  }
  return {
    kind: form.kind,
    ...(form.kind === 'person' && Object.keys(person).length > 0 ? { person } : {}),
    ...(form.kind === 'organization' && trimmed.legalName.length > 0
      ? { organization: { legalName: trimmed.legalName } }
      : {}),
    ...(trimmed.email.length > 0 ? { email: trimmed.email } : {}),
    ...(trimmed.phone.length > 0 ? { phone: trimmed.phone } : {}),
    ...(form.address === undefined ? {} : { address: addressBody(form.address) }),
    // Nur bei einer Person: works_for verbindet Person -> Organisation. Bei
    // einer Firma bleibt die fruehere Wahl stehen, wird aber nicht geschickt.
    ...(form.kind === 'person' && worksFor !== undefined ? { worksForPartyId: worksFor.partyId } : {}),
    // Die Funktion steht an der Kante (S1b) und gilt nur mit ihr; ohne Organisation
    // lehnte der Server sie mit field_not_allowed ab.
    ...(form.kind === 'person' && worksFor !== undefined && roleCode !== undefined ? { roleCode } : {}),
    // Die Leitung (S3, #786): nur an einer Person und nie neben worksForPartyId; der Server lehnte beides ab.
    ...(form.kind === 'person' && worksFor === undefined && leadsPartyId !== undefined ? { leadsPartyId } : {}),
    // Website, Handelsregister und USt-IdNr. nur an einer Organisation (S2); an einer Person lehnte der Server sie ab.
    ...(form.kind === 'organization' && angaben !== undefined ? organisationAngabenRumpf(angaben) : {}),
  };
}

/**
 * Die Anschrift der Anfrage. Leere Felder fehlen -- auch Pflichtfelder: die
 * Oberflaeche prueft die Pflichtangaben NICHT vorab (ADR-0056 Ziffer 8), der
 * Server nennt sie je Feld, und der Bildschirm uebersetzt. Ein leeres Land
 * (nicht eindeutig erkannt) wird so zu "Waehle ein Land aus der Liste."
 */
function addressBody(address: AddressFields): Record<string, string> {
  const body: Record<string, string> = {};
  for (const field of ['countryCode', 'addressLine1', 'addressLine2', 'postalCode', 'locality'] as const) {
    const value = address[field].trim();
    if (value.length > 0) {
      body[field] = value;
    }
  }
  return body;
}

/**
 * Ordnet einen Feldpfad dem Eingabefeld zu, das ihn traegt.
 *
 * DAS IST DER GRUND, WARUM DIE API UEBERHAUPT FELDPFADE LIEFERT. ADR-0019
 * Nachtrag Punkt 4 begruendet die Ausnahme von der Security-Baseline genau
 * damit: "ein Formular, das nur bad-request bekommt, kann den Fokus nicht auf
 * das fehlerhafte Feld setzen -- das ist ein Mangel gegen NFR-ACC-001, nicht
 * bloss Ergonomie". Ohne diese Zuordnung waere die Ausnahme bezahlt und nicht
 * eingeloest.
 *
 * Pfade auf das Sammelobjekt zeigen auf das erste Feld darin: `person` ist kein
 * Eingabefeld, aber "Vorname" ist die Stelle, an der die Nutzerin ansetzt.
 */
export const FIELD_INPUT_IDS: Readonly<Record<string, string>> = Object.freeze({
  kind: 'qc-art',
  displayName: 'qc-vorname',
  person: 'qc-vorname',
  'person.givenName': 'qc-vorname',
  'person.familyName': 'qc-nachname',
  organization: 'qc-firmenname',
  'organization.legalName': 'qc-firmenname',
  email: 'qc-email',
  phone: 'qc-telefon',
  address: 'qc-land',
  'address.usageCode': 'qc-land',
  'address.countryCode': 'qc-land',
  'address.addressLine1': 'qc-zeile1',
  'address.addressLine2': 'qc-zeile2',
  'address.postalCode': 'qc-plz',
  'address.locality': 'qc-ort',
  worksForPartyId: 'qc-arbeitet-fuer-suche',
  // Ein Fehler der Leitung fuehrt an ihre Gruppe in der Karte "Neu" (S3).
  leadsPartyId: 'qc-leitung',
  roleCode: 'qc-funktion',
  website: 'qc-org-website',
  commercialRegister: 'qc-org-register-nummer',
  'commercialRegister.issuer': 'qc-org-register-gericht',
  'commercialRegister.value': 'qc-org-register-nummer',
  vatId: 'qc-org-ustid',
  'vatId.issuer': 'qc-org-ustid',
  'vatId.value': 'qc-org-ustid',
});

export function inputIdFor(fieldPath: string): string | undefined {
  return FIELD_INPUT_IDS[fieldPath];
}

interface SubmitOptions {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly form: QuickCaptureForm;
  /** Die gewaehlte Organisation -- ein eigener Zustand neben dem Formular, den der Freitext nie schreibt. */
  readonly worksFor?: WorksForChoice | undefined;
  /** Die Funktion an "Arbeitet für" als Code der Werteliste (S1b); nur mit `worksFor`. */
  readonly roleCode?: string | undefined;
  /** Website, Handelsregister und USt-IdNr. der neuen Organisation (S2); nur bei `kind: organization`. */
  readonly organisationAngaben?: OrganisationAngaben | undefined;
  /** Die Organisation, die diese Person leitet (S3); nie zusammen mit `worksFor`. */
  readonly leadsPartyId?: string | undefined;
  /** Die Absendekennung (ADR-0060); dieselbe bei einer Wiederholung mit demselben Inhalt. */
  readonly clientRequestId?: string | undefined;
  /** Einspritzbar, damit der Test ohne Netz laeuft. */
  readonly send?: typeof fetch;
}

/**
 * Schickt die Anlage.
 *
 * Sie unterscheidet drei Ausgaenge, und die Unterscheidung ist die ganze
 * Arbeit: angelegt, fachlich abgelehnt mit Feldbezug, oder abgelehnt/gestoert
 * mit stabilem Grundcode. Ein gemeinsamer Fehlerzweig waere bequemer und
 * naehme der Oberflaeche genau die Auskunft, aus der ein naechster Schritt
 * folgt (NFR-UX-005).
 */
export async function submitQuickCapture(options: SubmitOptions): Promise<QuickCaptureResult> {
  const send = options.send ?? fetch;
  const response = await send(`${options.baseUrl}/parties`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${options.accessToken}`,
    },
    body: JSON.stringify({
      ...buildRequestBody(options.form, options.worksFor, options.roleCode, options.organisationAngaben, options.leadsPartyId),
      ...(options.clientRequestId === undefined ? {} : { clientRequestId: options.clientRequestId }),
    }),
  });
  const body: unknown = await response.json().catch(() => undefined);
  const record = (body ?? {}) as Record<string, unknown>;

  // 200: dieselbe Kennung mit demselben Inhalt -- das Original, nichts doppelt.
  if (response.status === 201 || response.status === 200) {
    const contacts = Array.isArray(record['contactPoints']) ? record['contactPoints'] : [];
    return {
      kind: 'created',
      partyId: typeof record['partyId'] === 'string' ? record['partyId'] : '',
      displayName: typeof record['displayName'] === 'string' ? record['displayName'] : '',
      contacts: contacts.map((entry) => ({
        channel: typeof (entry as Record<string, unknown>)['channel'] === 'string'
          ? String((entry as Record<string, unknown>)['channel'])
          : '',
      })),
      addressCreated: Array.isArray(record['postalAddresses']) && record['postalAddresses'].length > 0,
    };
  }

  if (response.status === 422 && Array.isArray(record['errors'])) {
    return {
      kind: 'invalid',
      errors: record['errors']
        .map((entry) => entry as Record<string, unknown>)
        .filter((entry) => typeof entry['fieldPath'] === 'string' && typeof entry['reasonCode'] === 'string')
        .map((entry) => ({ fieldPath: String(entry['fieldPath']), reasonCode: String(entry['reasonCode']) })),
    };
  }

  return {
    kind: 'rejected',
    reasonCode: typeof record['reasonCode'] === 'string' ? record['reasonCode'] : undefined,
    // Was die Antwort ueber sich selbst hergibt -- dieselben Fakten wie fuer die
    // Zugriffsentscheidung, und aus demselben Grund. Frueher stand hier ein
    // einzelnes `retryable`; es sagte die Wahrheit ueber die Wiederholbarkeit,
    // aber nichts darueber, ob eine Auskunft ankam und ob es eine
    // Vorgangsnummer gibt -- und genau daran haengen die Saetze des Rueckfalls.
    facts: {
      status: response.status,
      answered: typeof body === 'object' && body !== null,
      ...correlationOf(record),
    },
  };
}
